import { LogManager } from '@src/utility/Logger';

import { sameOriginalIndices } from '../../models/karaoke/CaptionLineBreak';
import { rebuildPhraseClips } from '../../services/syntheticCaptionBreaks';
import { AbstractCommand } from '../AbstractCommand';

import type { CaptionLineBreak } from '../../models/karaoke/CaptionLineBreak';
import type { KaraokeClip } from '../../models/karaoke/KaraokeClip';
import type { VideoEditor } from '../../VideoEditor';

const logger = LogManager.getLogger('karaoke:syntheticBreak');

/**
 * PLAT-9623: move an existing synthetic break to a new `textPosition`.
 * Used by the drag handle. Recomputes the affected phrase's clip set
 * with the updated record. No-op (returns false) if `textPosition` is
 * unchanged so live drag streams don't pollute the undo stack.
 */
export class AdjustSyntheticCaptionBreakCommand extends AbstractCommand {
  private breakId: string;
  private nextTextPosition: number;
  private _shouldCreateUndoRedoEntry: boolean;

  private prevClips: KaraokeClip[] | null = null;
  private prevBreaks: CaptionLineBreak[] | null = null;

  constructor(
    editor: VideoEditor,
    breakId: string,
    nextTextPosition: number,
    options?: { createUndoRedoEntry?: boolean },
  ) {
    super(editor);
    this.breakId = breakId;
    this.nextTextPosition = nextTextPosition;
    this._shouldCreateUndoRedoEntry = options?.createUndoRedoEntry ?? true;
  }

  override shouldCreateUndoRedoEntry(): boolean {
    return this._shouldCreateUndoRedoEntry;
  }

  getName(): string {
    return 'AdjustSyntheticCaptionBreakCommand';
  }

  getDescription(): string {
    return `Adjust synthetic caption break ${this.breakId} → ${this.nextTextPosition}`;
  }

  execute(): boolean {
    const track = this.editor.getKaraokeTrack();
    if (!track) return false;

    const target = track.syntheticBreaks.find(b => b.id === this.breakId);
    if (!target) {
      logger.debug('execute: break not found', { breakId: this.breakId });
      return false;
    }
    if (target.textPosition === this.nextTextPosition) {
      // No-op — keep the undo stack clean during live drag.
      return false;
    }

    const prevClips = [...track.karaokeClips];
    const prevBreaks = [...track.syntheticBreaks];

    const updatedRecord: CaptionLineBreak = {
      ...target,
      textPosition: this.nextTextPosition,
    };
    const updatedBreaks = prevBreaks.map(b =>
      b.id === this.breakId ? updatedRecord : b,
    );
    const breaksForPhrase = updatedBreaks.filter(b =>
      sameOriginalIndices(
        b.phraseOriginalIndices,
        target.phraseOriginalIndices,
      ),
    );

    const rebuilt = rebuildPhraseClips({
      prevClips,
      phraseOriginalIndices: target.phraseOriginalIndices,
      breaksForPhrase,
    });
    if (!rebuilt) {
      logger.debug('execute: phrase rebuild refused', {
        breakId: this.breakId,
        nextTextPosition: this.nextTextPosition,
      });
      return false;
    }

    // Commit undo state only after the rebuild succeeded.
    this.prevClips = prevClips;
    this.prevBreaks = prevBreaks;

    const minIdx = rebuilt.phraseClipIndices[0];
    const maxIdx =
      rebuilt.phraseClipIndices[rebuilt.phraseClipIndices.length - 1];
    track.karaokeClips = [
      ...prevClips.slice(0, minIdx),
      ...rebuilt.replacementClips,
      ...prevClips.slice(maxIdx + 1),
    ];
    track.syntheticBreaks = updatedBreaks;

    this.editor.markDirty('synthetic_caption_break_adjusted');
    return true;
  }

  undo(): boolean {
    if (!this.prevClips || !this.prevBreaks) return false;
    const track = this.editor.getKaraokeTrack();
    if (!track) return false;
    track.karaokeClips = this.prevClips;
    track.syntheticBreaks = this.prevBreaks;
    this.editor.markDirty('synthetic_caption_break_adjusted_undo');
    return true;
  }
}
